import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  useTheme,
  Typography,
  Snackbar,
  LinearProgress,
} from '@mui/material'
import { type GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { useAppTheme } from '@/theme/ThemeContext'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import { useScopedApiFilters, useTableStore } from '@/store/table/table'
import {
  getConsumers,
  getConsumerExportRows,
  ConsumerRow,
  type ApiResourceFilter,
} from '@/services/consumerService'
import { exportToExcel, exportToPDF, copyToClipboard, exportableColumns } from '@/utils/exportUtils'
import { buildConsumerColumns } from '@/utils/consumerColumns'
import { useIsRimonimStaff } from '@/hooks/useIsRimonimStaff'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { useSearchParams } from 'react-router-dom'
import { getNoRowsLabel } from '@/utils/tableEmptyState'
import { isolateNumber } from '@/utils/numberDirection'
import SendNotificationDialog from '@/components/notification-send/SendNotificationDialog'
import type { SendAudience } from '@/services/notificationService'
import { useSearch } from '@/hooks/useSearch'
import { debugLog } from '@/utils/debugLog'

// Selection model shape used by the DataGrid wrapper (src/components/Table/DataGrid.tsx:24) —
// 'exclude' means "every loaded row except these ids" (produced by the header select-all).
type RowSelectionModelV7 = {
  type: 'include' | 'exclude'
  ids: Set<string | number>
}

// What the send button reaches. A selection wins. With no selection, an active
// search or column filter reaches every result on every page. With neither, no
// send: a send to the whole site must not be one click away.
type SendTarget =
  | { kind: 'none' }
  | { kind: 'selection'; premiseIds: number[] }
  | { kind: 'search'; filters: ApiResourceFilter[]; total: number }

const sendAudienceOf = (target: SendTarget): SendAudience => {
  switch (target.kind) {
    case 'none':
      return { kind: 'premises', ids: [] }
    case 'selection':
      return { kind: 'premises', ids: target.premiseIds }
    case 'search':
      return { kind: 'search', filters: target.filters }
  }
}

const sendLabelOf = (target: SendTarget) => {
  switch (target.kind) {
    case 'none':
      return 'שליחת הודעה'
    case 'selection':
      return `שליחת הודעה (${target.premiseIds.length})`
    case 'search':
      return `שליחת הודעה (${target.total})`
  }
}

// A search send reaches every result on every page; say so when the page shows fewer.
const audienceWarningOf = (target: SendTarget, shownRows: number) =>
  target.kind === 'search' && target.total > shownRows ? (
    <>
      שימו לב: ההודעה תישלח ל-{isolateNumber(target.total)} צרכנים, יותר מ-
      {isolateNumber(shownRows)} הצרכנים שמוצגים בעמוד.
    </>
  ) : undefined

const Consumers: React.FC = () => {
  const theme = useTheme()
  const apiFilters = useScopedApiFilters('consumers')
  const isStaff = useIsRimonimStaff(localStorage.getItem('accountId'))
  const { setFilteredRowsStore, clearApiFilters } = useTableStore()


  // Data state
  const [rows, setRows] = useState<ConsumerRow[]>([])
  const [resourceNames, setResourceNames] = useState<string[]>([])
  const [resourceHeaders, setResourceHeaders] = useState<Record<string, string>>({})
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [totalRows, setTotalRows] = useState(0)

  // Send-message-to-selection (Task 7): selection is derived from the loaded page's
  // rows filtered against the grid's include/exclude model, never assumed as a plain array.
  const [rowSelectionModel, setRowSelectionModel] =
    useState<RowSelectionModelV7>({
      type: 'include',
      ids: new Set<string | number>(),
    })
  const [sendOpen, setSendOpen] = useState(false)

  const selectedIds = useMemo<number[]>(() => {
    if (rowSelectionModel.type === 'include') {
      return rows
        .filter(r => rowSelectionModel.ids.has(r.id))
        .map(r => Number(r.id))
    }
    return rows
      .filter(r => !rowSelectionModel.ids.has(r.id))
      .map(r => Number(r.id))
  }, [rows, rowSelectionModel])

  const { langKey } = useAppTheme()
  const apiRef = useGridApiRef()

  // --- URL IS the single source of truth for table state ---
  const [searchParams, setSearchParams] = useSearchParams()

  // Read state directly from URL every render (NO useState needed)
  const page = parseInt(searchParams.get('page') || '0', 10)
  const sortBy = searchParams.get('sortBy') || 'premiseId'
  const sortOrder = (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC'
  const urlSearch = searchParams.get('search') || ''
  const urlFilters = searchParams.get('filters') || ''

  // Only pageSize uses local state (not critical to persist in URL)
  const [pageSize, setPageSize] = useState(() => getTablePageSize(25))

  // Sort model derived from URL state
  const sortModel = useMemo<GridSortModel>(() => [
    { field: sortBy, sort: sortOrder.toLowerCase() as 'asc' | 'desc' }
  ], [sortBy, sortOrder])

  // Helper to update URL params (replaces setState for page/sort)
  const updateUrlParams = useCallback((updates: Record<string, string>, removals?: string[]) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      Object.entries(updates).forEach(([k, v]) => params.set(k, v))
      if (removals) removals.forEach(k => params.delete(k))
      return params
    }, { replace: true })
  }, [setSearchParams])

  // Wrapper setters that update the URL instead of local state
  const setPage = useCallback((p: number) => updateUrlParams({ page: p.toString() }), [updateUrlParams])


  // The text waits for a pause, a newer request stops the older one, and only the newest answer lands.
  // A new search starts from the first page in the same tick.
  const search = useSearch({
    initial: urlSearch,
    onQueryChange: () => setPage(0),
  })
  const debouncedSearch = search.query || undefined

  // Handler for clearing all filters (used by header button)
  const handleGlobalClear = () => {
    clearApiFilters()
    setFilteredRowsStore({})
    search.searchNow('')
    updateUrlParams({ page: '0' }, ['search', 'filters'])
  }

  // Build columns dynamically: ordered, translated, filtered for empty, with special renderers
  const dynamicColumns = useMemo(() =>
    buildConsumerColumns({
      rows,
      resourceNames,
      resourceHeaders,
      langKey,
      arrivalDateFilter: isStaff,
    }),
    [resourceNames, rows, resourceHeaders, langKey, isStaff]
  )

  // Get visible columns (in display order) with translated headers from the DataGrid
  const getVisibleExportColumns = useCallback(() => {
    try {
      const api = apiRef.current
      if (!api) return exportableColumns(dynamicColumns.map((c: any) => ({ headerName: c.headerName, field: c.field })))
      return exportableColumns(
        api.getVisibleColumns()
          .filter((c: any) => !c.field.startsWith('__'))
          .map((c: any) => ({ headerName: c.headerName || c.field, field: c.field }))
      )
    } catch {
      return exportableColumns(dynamicColumns.map((c: any) => ({ headerName: c.headerName, field: c.field })))
    }
  }, [apiRef, dynamicColumns])

  // Track if initial data has been loaded
  const [hasMounted, setHasMounted] = useState(false)

  // Restore search & filters from URL on mount
  useEffect(() => {
    // Restore search from URL
    if (urlSearch) {
      setFilteredRowsStore({
        searchFilter: { label: 'חיפוש', value: urlSearch },
      })
    }

    // Restore column filters from URL
    if (urlFilters) {
      try {
        const parsed = JSON.parse(urlFilters) as { name: string; value: string[]; operator?: string }[]
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsed.forEach(f => useTableStore.getState().updateFieldFilter(f.name, f.value, f.operator as any))
        }
      } catch { /* invalid JSON, ignore */ }
    }

    // Only clear if coming fresh (no URL state)
    if (!urlSearch && !urlFilters && window.location.search.length <= 1) {
      clearApiFilters()
      setFilteredRowsStore({})
    }
    
    setHasMounted(true)
  }, [])

  // Reset page to 0 when filters or search ACTUALLY change (not on mount)
  const prevApiFiltersRef = useRef(apiFilters)
  const prevSearchRef = useRef(debouncedSearch)

  useEffect(() => {
    const filtersChanged = prevApiFiltersRef.current !== apiFilters
    const searchChanged = prevSearchRef.current !== debouncedSearch
    
    debugLog('[Consumers] Sync Effect Check:', { 
      filtersChanged, 
      searchChanged, 
      hasMounted,
      apiFilters: apiFilters.length,
      debouncedSearch 
    })

    prevApiFiltersRef.current = apiFilters
    prevSearchRef.current = debouncedSearch

    if (hasMounted && (filtersChanged || searchChanged)) {
      debugLog('[Consumers] Triggering URL sync and reset to page 0')
      setPage(0)

      // Sync search to URL
      const searchUpdate: Record<string, string> = {}
      const removals: string[] = []
      if (debouncedSearch) {
        searchUpdate.search = debouncedSearch
      } else {
        removals.push('search')
      }

      // Sync filters to URL
      if (apiFilters.length > 0) {
        searchUpdate.filters = JSON.stringify(apiFilters)
      } else {
        removals.push('filters')
      }

      if (Object.keys(searchUpdate).length > 0 || removals.length > 0) {
        updateUrlParams({ ...searchUpdate, page: '0' }, removals)
      }
    }
  }, [apiFilters, debouncedSearch, hasMounted, setPage, updateUrlParams])

  // Reset selection whenever the underlying row set changes (Task 7 review
  // fix): the grid's 'exclude' model is "every loaded row NOT in ids", so a
  // stale selection made on a previous page/sort/filter would silently
  // re-apply to whatever new rows land after a refetch. Keyed on the same
  // query inputs as the fetch effect below (not on `rows` or
  // `rowSelectionModel` themselves) so it fires only when the row set is
  // genuinely different, not on every incidental re-render, and so it never
  // wipes a selection the user just made on the current page.
  useEffect(() => {
    setRowSelectionModel({ type: 'include', ids: new Set<string | number>() })
  }, [page, pageSize, sortBy, sortOrder, apiFilters, debouncedSearch, langKey])

  // The filters of the list request: column filters plus the search box.
  // The exports and the send to a search use this same list.
  const listFilters = useMemo(() => {
    const filters = [...apiFilters]
    if (debouncedSearch) {
      filters.push({ name: 'search', value: [debouncedSearch] })
    }
    return filters
  }, [apiFilters, debouncedSearch])

  // Fetch consumers with server-side pagination and search
  useEffect(() => {
    if (!hasMounted) return

    const accountId = localStorage.getItem('accountId')
    if (!accountId) {
      console.warn('[Consumers] No accountId found, redirecting...')
      window.location.href = '/login'
      return
    }

    setError(null)
    search.run(
      async signal => {
        const result = await getConsumers(
          {
            accountId: Number(accountId),
            page: page + 1,
            size: pageSize,
            sortBy,
            sortOrder,
            langKey,
          },
          listFilters,
          signal
        )
        // No column metadata yet (first fetch returned 0 rows): fetch 1 row without filters
        // to establish the column structure.
        const columns =
          result.resourceNames.length === 0 && resourceNames.length === 0
            ? await getConsumers(
                { accountId: Number(accountId), page: 1, size: 1, langKey },
                [],
                signal
              )
            : result
        return { result, columns }
      },
      ({ result, columns }) => {
        setRows(result.rows)
        setTotalRows(result.total)
        if (columns.resourceNames.length > 0) {
          setResourceNames(columns.resourceNames)
          setResourceHeaders(columns.resourceHeaders)
        }
      },
      err => {
        console.error('Failed to fetch consumers:', err)
        setError('שגיאה בטעינת הנתונים')
      }
    )
  }, [hasMounted, page, pageSize, sortBy, sortOrder, listFilters, langKey])

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage)
    setPageSize(newPageSize)
  }, [setPage])

  const handleSortChange = useCallback((field: string, order: 'ASC' | 'DESC') => {
    updateUrlParams({ sortBy: field, sortOrder: order, page: '0' })
  }, [updateUrlParams])

  // Simulated progress timer ref
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  const sendTarget = useMemo<SendTarget>(() => {
    if (selectedIds.length > 0) {
      return { kind: 'selection', premiseIds: selectedIds }
    }
    if (listFilters.length > 0 && totalRows > 0 && !search.isSearching) {
      return { kind: 'search', filters: listFilters, total: totalRows }
    }
    return { kind: 'none' }
  }, [selectedIds, listFilters, totalRows, search.isSearching])

  // Excel export — client-side with visible columns only
  const handleExcelExport = useCallback(async () => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId || totalRows === 0) return

    setIsExporting(true)
    setExportProgress(0)
    stopProgressTimer()

    // Simulate progress while fetching all rows
    let simulated = 0
    progressTimerRef.current = setInterval(() => {
      simulated += (95 - simulated) * 0.04
      setExportProgress((prev) => {
        const next = Math.round(Math.max(prev, simulated))
        return Math.min(next, 95)
      })
    }, 300)

    try {
      const exportCols = getVisibleExportColumns()
      const exportRows = await getConsumerExportRows(
        { accountId: Number(accountId), page: 1, size: totalRows, langKey },
        listFilters,
        exportCols.map(c => c.field)
      )

      exportToExcel(exportCols, exportRows)

      // Download complete — fill to 100%
      stopProgressTimer()
      setExportProgress(100)
      // Brief pause at 100% so the user sees it complete
      await new Promise(resolve => setTimeout(resolve, 600))
    } catch (err: any) {
      console.error('Excel export failed:', err)
      console.error('Error details:', err?.response?.status, err?.message)
    } finally {
      stopProgressTimer()
      setIsExporting(false)
      setExportProgress(0)
    }
  }, [totalRows, langKey, stopProgressTimer, listFilters, getVisibleExportColumns])

  // PDF / Copy — client-side (fetch all rows then export with visible columns only)
  const handleExportAll = useCallback(async (format: 'pdf' | 'copy') => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId || totalRows === 0) return

    try {
      const exportCols = getVisibleExportColumns()

      if (format === 'pdf') {
        const result = await getConsumers(
          { accountId: Number(accountId), page: 1, size: totalRows, langKey },
          listFilters
        )
        exportToPDF(exportCols, result.rows)
      } else {
        // Copy the first page of the table
        const result = await getConsumers(
          { accountId: Number(accountId), page: 1, size: pageSize, langKey },
          listFilters
        )
        copyToClipboard(exportCols, result.rows)
      }
    } catch (err) {
      console.error('Export failed:', err)
    }
  }, [totalRows, pageSize, langKey, listFilters, getVisibleExportColumns])

  // The header reads its text back from the store, so the store holds what was typed.
  const rememberSearch = (value: string) =>
    setFilteredRowsStore(
      value.trim() ? { searchFilter: { label: 'חיפוש', value } } : {}
    )

  const changeSearchText = (value: string) => {
    rememberSearch(value)
    search.setText(value)
  }

  const executeSearch = (value: string) => {
    rememberSearch(value)
    search.searchNow(value)
  }

  // Distinct name (not `accountId`) to avoid shadowing the per-function locals read
  // elsewhere in this file (fetchConsumers/handleExcelExport/handleExportAll).
  // Guarded like fetchConsumers' own accountId check above: read the raw
  // value first so a missing/invalid accountId becomes NaN instead of a
  // silent 0 (Number(null) === 0) that could reach the dialog as if it were
  // a real account id.
  const notificationAccountIdRaw = localStorage.getItem('accountId')
  const notificationAccountId = notificationAccountIdRaw
    ? Number(notificationAccountIdRaw)
    : NaN

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <PageHeaderWithIcons
        title="טבלת צרכנים"
        showSearch
        proSearch
        executeSearch={executeSearch}
        onSearchChange={changeSearchText}
        onClearFilters={handleGlobalClear}
        onExcelExport={handleExcelExport}
        onPdfExport={() => handleExportAll('pdf')}
      />
      <Box
        sx={{
          width: '95%',
          display: 'flex',
          justifyContent: 'flex-end',
          direction: 'rtl',
          pb: 1,
        }}
      >
        <Button
          variant="contained"
          size="small"
          disabled={sendTarget.kind === 'none' || !notificationAccountId}
          onClick={() => setSendOpen(true)}
        >
          {sendLabelOf(sendTarget)}
        </Button>
      </Box>
      <Box sx={{ width: '95%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <DataGridPremiumDemo
            width="100%"
            maxHeight="100%"
            rows={rows}
            columns={dynamicColumns}
            hideFooter={true}
            headerPosition="bottom"
            pageSize={pageSize}
            serverSideRowCount={totalRows}
            serverSidePage={page}
            onPaginationChange={handlePaginationChange}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            onSortChange={handleSortChange}
            externalSortModel={sortModel}
            apiRefExternal={apiRef}
            noRowsLabel={getNoRowsLabel(debouncedSearch)}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            loading={search.isSearching}
          />
        )}
      </Box>

      <SendNotificationDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        accountId={notificationAccountId}
        audience={sendAudienceOf(sendTarget)}
        audienceWarning={audienceWarningOf(sendTarget, rows.length)}
      />

      {/* Export progress indicator */}
      <Snackbar
        open={isExporting}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: theme.palette.mode === 'dark' ? '#1D325A' : '#fff',
            color: theme.palette.text.primary,
            px: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 300,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                מייצא קובץ Excel...
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'left' }}>
                {exportProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{
                borderRadius: 1,
                height: 6,
                bgcolor: theme.palette.mode === 'dark' ? '#374151' : '#E4E7EB',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#81B5FF',
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        </Box>
      </Snackbar>
    </Box>
  )
}

export default Consumers
